import { useState, useEffect } from 'react';

/**
 * @callback useFormFieldsCallback
 * @param fieldId
 *   Id of the field being set.
 * @param fieldValue
 *   Value of field being set.
 */
/**
 * Returns a custom setter for form fields.
 *
 * If all fields should be set in one go pass an object with all data as
 * the fields param i.e. {fields: {<all fields>}}
 *
 * To set multiple fields in one go call it with the partialFields param:
 * {patialFields: {<some or all fields>}}
 *
 * @param {array} initialState
 *   Initial state.
 * @param {useFormFieldsCallback} callback
 *   Callback function to run after setting fields.
 */
export function useFormFields(initialState, callback = null) {
  const [fields, setValues] = useState(initialState);

  return [
    fields,
    function(event) {
      if (callback !== null) {
        callback(event.target.id, event.target.value);
      }
      if(event.hasOwnProperty('fields')) {
        setValues(event.fields);
      }
      // Allow manually sending one or multiple field values by calling as:
      else if(event.hasOwnProperty('partialFields')) {
        setValues({
          ...fields,
          ...event.partialFields
        });
      }
      else {
        // We may be called by a checkbox.
        if(event.target.hasOwnProperty('checked')) {
          setValues({
            ...fields,
            [event.target.id]: event.target.checked
          });
        }
        // Or any other object.
        else {
          setValues({
            ...fields,
            [event.target.id]: event.target.value
          });
        }
      }
    }
  ];
}

/**
 * Manually set a field value.
 *
 * @param {function} handleFieldChange - Funciton to call.
 * @param {string} id - Field id.
 * @param {*} value - Value to set.
 */
export function setFormField(handleFieldChange, id, value) {
  handleFieldChange(
    {
      target: {
        id: id,
        value: value,
      }
    }
  );
}

/**
 * Storesa values perisstently in localstorage.
 *
 * https://www.joshwcomeau.com/react/persisting-react-state-in-localstorage/
 * Use like this:
 * const [
 *   value,
 *   setValue
 * ] = useStickyState(<default>, "<key>");
 *
 * @param {*} defaultValue
 *    Defaultr value to return if no value found in storage.
 * @param {*} key 
 *    Key to store value in
 *
 * @returns
 *    Value.
 */
export function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    let stickyValue = null;
    try {
      stickyValue = window.localStorage.getItem(key);
    }
    catch (error) {
      console.error(error);
    }
    return stickyValue !== null
      ? JSON.parse(stickyValue)
      : defaultValue;
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
    catch (error) {
      console.error(error);
    }
  }, [key, value]);
  return [value, setValue];
}
